import { createAsyncThunk } from '@reduxjs/toolkit';
import { SelectOption } from 'src/types/SelectOption';
import {
  isValidCode,
  thunkExecuteAndReturnResultOrShowError,
} from '../../services/base.service';
import {
  deleteDyslexiaScreenerFile,
  getDyslexiaScreenerById,
  getDyslexiaScreenerContact,
  getDyslexiaScreenerFilesBySchool,
  getDyslexiaScreenerFormViewModel,
  getDyslexiaScreenerLeas,
  getDyslexiaScreenerListItems,
  getDyslexiaScreenerSchoolsByLea,
  getDyslexiaScreenerSchoolYears,
  postDyslexiaScreenerContact,
  putDyslexiaScreenerContact,
  uploadDyslexiaScreenerFile,
} from '../../services/dyslexiaScreener.service';
import { expandDyslexiaScreenerListItemsToDisplayRows } from '../../services/dyslexiaScreenerListDisplay.service';
import { DyslexiaScreenerContactModel } from '../../types/DyslexiaScreenerContactModel';
import { DyslexiaScreenerFileListItemModel } from '../../types/DyslexiaScreenerFileListItemModel';
import { DyslexiaScreenerFormViewModel } from '../../types/DyslexiaScreenerFormViewModel';
import { DyslexiaScreenerListResponseModel } from '../../types/DyslexiaScreenerListResponseModel';
import { DyslexiaScreenerModel } from '../../types/DyslexiaScreenerModel';

export const fetchDyslexiaScreenerById = createAsyncThunk(
  'dyslexiaScreener/fetchDyslexiaScreenerById',
  async (id: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<DyslexiaScreenerModel>(
      thunkAPI,
      getDyslexiaScreenerById(id, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchDyslexiaScreenerFormViewModel = createAsyncThunk(
  'dyslexiaScreener/fetchDyslexiaScreenerFormViewModel',
  async (schoolYear: string | undefined, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<DyslexiaScreenerFormViewModel>(
      thunkAPI,
      getDyslexiaScreenerFormViewModel(schoolYear, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchDyslexiaScreenerListItems = createAsyncThunk(
  'dyslexiaScreener/fetchDyslexiaScreenerListItems',
  async (schoolYear: string, thunkAPI) => {
    const body =
      await thunkExecuteAndReturnResultOrShowError<DyslexiaScreenerListResponseModel>(
        thunkAPI,
        getDyslexiaScreenerListItems(schoolYear, {
          signal: thunkAPI.signal as AbortSignal,
        })
      );
    const raw = body as unknown as Record<string, unknown>;
    const listItems = Array.isArray(body.listItems)
      ? body.listItems
      : Array.isArray(raw.ListItems)
      ? (raw.ListItems as DyslexiaScreenerListResponseModel['listItems'])
      : [];
    const dyslexiaScreenerUpdate =
      body.dyslexiaScreenerUpdate === true ||
      raw.DyslexiaScreenerUpdate === true;
    return {
      displayRows: expandDyslexiaScreenerListItemsToDisplayRows(
        listItems,
        dyslexiaScreenerUpdate
      ),
      dyslexiaScreenerUpdate,
    };
  }
);

export const fetchDyslexiaScreenerContact = createAsyncThunk(
  'dyslexiaScreener/fetchDyslexiaScreenerContact',
  async (
    options: { showLoading?: boolean } = { showLoading: true },
    thunkAPI
  ) => {
    try {
      const response = await getDyslexiaScreenerContact({
        signal: thunkAPI.signal as AbortSignal,
      });

      if (response.status === 404) {
        return {
          data: {
            id: '',
            leaIdentifier: '',
            firstName: '',
            lastOrSurname: '',
            email: '',
            telephone: '',
          } as DyslexiaScreenerContactModel,
          showLoading: options.showLoading,
        };
      }

      if (!isValidCode(response.status)) {
        if (!response.aborted) {
          return await thunkExecuteAndReturnResultOrShowError<{ id: string }>(
            thunkAPI,
            Promise.resolve(response)
          );
        }
        return {
          data: {
            id: '',
            leaIdentifier: '',
            firstName: '',
            lastOrSurname: '',
            email: '',
            telephone: '',
          } as DyslexiaScreenerContactModel,
          showLoading: options.showLoading,
        };
      }

      return { data: response.data, showLoading: options.showLoading };
    } catch (error) {
      console.warn(
        'Failed to fetch dyslexia screener contact, using default values:',
        error
      );
      return {
        data: {
          id: '',
          leaIdentifier: '',
          firstName: '',
          lastOrSurname: '',
          email: '',
          telephone: '',
        } as DyslexiaScreenerContactModel,
        showLoading: options.showLoading,
      };
    }
  }
);

export const fetchDyslexiaScreenerSchoolYears = createAsyncThunk(
  'dyslexiaScreener/fetchDyslexiaScreenerSchoolYears',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getDyslexiaScreenerSchoolYears({ signal: thunkAPI.signal })
    );
  }
);

export const fetchDyslexiaScreenerLeas = createAsyncThunk(
  'dyslexiaScreener/fetchDyslexiaScreenerLeas',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getDyslexiaScreenerLeas({ signal: thunkAPI.signal as AbortSignal })
    );
  }
);

export const fetchDyslexiaScreenerSchoolsByLeaId = createAsyncThunk(
  'dyslexiaScreener/fetchDyslexiaScreenerSchoolsByLeaId',
  async (leaId: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getDyslexiaScreenerSchoolsByLea(leaId, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const createDyslexiaScreenerContact = createAsyncThunk(
  'dyslexiaScreener/createDyslexiaScreenerContact',
  async (contact: DyslexiaScreenerContactModel, thunkAPI): Promise<string> => {
    return await thunkExecuteAndReturnResultOrShowError<string>(
      thunkAPI,
      postDyslexiaScreenerContact(contact, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const updateDyslexiaScreenerContact = createAsyncThunk(
  'dyslexiaScreener/updateDyslexiaScreenerContact',
  async (contact: DyslexiaScreenerContactModel, thunkAPI): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      putDyslexiaScreenerContact(contact, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const uploadDyslexiaScreenerFileThunk = createAsyncThunk(
  'dyslexiaScreener/uploadFile',
  async (
    params: {
      schoolYear: string;
      schoolIdentifier: string;
      leaIdentifier: string;
      file: File;
      documentType: string;
    },
    thunkAPI
  ): Promise<{ id: string }> => {
    const response = await uploadDyslexiaScreenerFile(
      params.schoolYear,
      params.schoolIdentifier,
      params.leaIdentifier,
      params.file,
      params.documentType,
      {
        signal: thunkAPI.signal as AbortSignal,
      }
    );

    if (!isValidCode(response.status)) {
      const errorMessage =
        response.error?.detail ||
        response.error?.title ||
        'Failed to upload file. Please try again.';
      throw new Error(errorMessage);
    }

    return response.data;
  }
);

export const deleteDyslexiaScreenerFileThunk = createAsyncThunk(
  'dyslexiaScreener/deleteFile',
  async (params: { leaId: string; id: string }, thunkAPI): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      deleteDyslexiaScreenerFile(params.leaId, params.id, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchDyslexiaScreenerFilesBySchoolThunk = createAsyncThunk(
  'dyslexiaScreener/fetchFilesBySchool',
  async (
    params: {
      schoolYear: string;
      leaId: string;
      schoolIdentifier: string;
      showLoading?: boolean;
    },
    thunkAPI
  ): Promise<DyslexiaScreenerFileListItemModel[]> => {
    return await thunkExecuteAndReturnResultOrShowError<
      DyslexiaScreenerFileListItemModel[]
    >(
      thunkAPI,
      getDyslexiaScreenerFilesBySchool(
        params.schoolYear,
        params.leaId,
        params.schoolIdentifier,
        {
          signal: thunkAPI.signal as AbortSignal,
        }
      )
    );
  }
);
